import React, {useState, useEffect} from "react";
import TextField from "@material-ui/core/TextField";
import {Autocomplete} from "@material-ui/lab";

import ActionArgs from "../../action_args";
import ActionArgument from "../../action_argument";

import {Topic} from "common/types/topic";
import {
  Prompt,
  Workflow,
  WorkflowContext,
  WorkflowInputs,
} from "common/flowmaster/types/workflow";
import {
  ActionDefinitionWithType,
  ActionSelectorArgument,
  TaskArgument,
  WorkflowTask,
} from "common/flowmaster/types/task_config";
import {OrganisationId} from "common/types/organisation";

interface ActionSelectorProps {
  argValue: ActionSelectorArgument;
  onUpdateItem: (value: ActionSelectorArgument) => void;
  actionDefinitions: ActionDefinitionWithType[];
  handleHasError?: (hasError: boolean) => void;
  disabled?: boolean;
  priorTasks?: WorkflowTask[];
  futureTasks?: WorkflowTask[];
  context: WorkflowContext;
  inputs: WorkflowInputs;
  prompts: Prompt[];
  workflows: Workflow[];
  organisationId: OrganisationId;
  embeddingTopics: Topic[];
}

function getDefaultArgValue(argType: string) {
  const ActionArg = ActionArgs[argType];
  return ActionArg?.default?.value ?? null;
}

function initialiseArgs(
  action: string,
  actionDefinitions: ActionDefinitionWithType[],
): unknown[] {
  const actionDefinition = actionDefinitions.find(({key}) => key === action);

  if (!actionDefinition?.args) {
    return [];
  }

  return actionDefinition.args.map(arg => getDefaultArgValue(arg.type));
}

const ActionSelectorEditor: React.FC<ActionSelectorProps> = ({
  argValue,
  onUpdateItem,
  actionDefinitions,
  handleHasError,
  disabled = false,
  priorTasks = [],
  futureTasks = [],
  context,
  inputs,
  prompts = [],
  workflows,
  organisationId,
  embeddingTopics = [],
}) => {
  const actionKey = argValue?.value?.actionKey || null;
  const selectedActionDef = actionDefinitions.find(
    def => def.key === actionKey,
  );
  const mappableActions = actionDefinitions.filter(
    def => def.mappable === true,
  );

  const getInitialisedArgs = () => {
    if (!selectedActionDef?.args) {
      return [];
    }

    return selectedActionDef.args.map((argDef, index) => {
      const existingArgs = argValue?.value?.actionArgs;
      if (Array.isArray(existingArgs) && index < existingArgs.length) {
        return existingArgs[index];
      }

      return getDefaultArgValue(argDef.type);
    });
  };

  const [currentArgs, setCurrentArgs] = useState<unknown[]>(
    getInitialisedArgs(),
  );

  useEffect(() => {
    setCurrentArgs(getInitialisedArgs());
  }, [argValue, selectedActionDef]);

  useEffect(() => {
    handleHasError?.(!actionKey);
  }, [actionKey, handleHasError]);

  const handleActionUpdate = (newActionKey: string | null) => {
    if (!newActionKey) {
      return;
    }

    const newArgs = initialiseArgs(newActionKey, actionDefinitions);
    setCurrentArgs(newArgs);

    onUpdateItem({
      value: {
        actionKey: newActionKey,
        actionArgs: newArgs,
      },
    });
  };

  const handleArgUpdate = (index: number, value: TaskArgument) => {
    if (!actionKey) {
      return;
    }

    const newArgs = [...currentArgs];
    newArgs[index] = value?.value ?? value;
    setCurrentArgs(newArgs);

    onUpdateItem({
      value: {
        actionKey,
        actionArgs: newArgs,
      },
    });
  };

  return (
    <div style={{padding: ".5em", marginBottom: ".25em"}}>
      <Autocomplete
        style={{width: "100%", marginBottom: "16px"}}
        value={selectedActionDef ?? null}
        onChange={(_, newActionDefinition) => {
          handleActionUpdate(newActionDefinition?.key ?? null);
        }}
        options={mappableActions}
        getOptionLabel={actionDefinition => actionDefinition.name}
        renderInput={params => (
          <TextField {...params} label="Action" variant="standard" />
        )}
        groupBy={actionDefinition => actionDefinition.group}
        disabled={disabled}
      />

      {selectedActionDef?.args?.map((argDefinition, index) => (
        <div
          key={argDefinition.key}
          style={{
            margin: "0.5em",
            padding: "0.5em",
            borderRadius: "4px",
          }}
        >
          <ActionArgument
            argDefinition={argDefinition}
            argValue={{value: currentArgs[index]} as TaskArgument}
            index={index}
            onUpdateItem={value => handleArgUpdate(index, value)}
            priorTasks={priorTasks}
            futureTasks={futureTasks}
            context={context}
            inputs={inputs}
            prompts={prompts}
            actionDefinitions={actionDefinitions}
            workflows={workflows}
            organisationId={organisationId}
            embeddingTopics={embeddingTopics}
            handleHasError={() => {}}
            disabled={disabled}
            useListItem={true}
          />
        </div>
      ))}
    </div>
  );
};

const ActionArg = {
  Component: ActionSelectorEditor,
  default: {
    value: {
      actionKey: null,
      actionArgs: [],
    },
  },
};

export default ActionArg;
